import React, { useEffect } from 'react';
import { BrowserRouter as Router, useNavigate } from 'react-router-dom';
import { ThemeProvider } from './theme/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { UserProvider } from './context/UserContext';
import { ProductProvider } from './context/ProductContext';
import { OrderProvider } from './context/OrderContext';
import { CommunityProvider } from './context/CommunityContext';
import { DashboardNotificationProvider } from './context/DashboardNotificationContext';
import AuthHandler from './context/AuthHandler';
import AppRoutes from './routes';
import { NavigationService } from './services/navigationService';
import { ErrorService, LoadingService, NotificationService } from './services/errorService';
import NotificationProvider from './components/common/NotificationProvider';
import LoadingOverlay from './components/common/LoadingOverlay';
import LoadingScreen from './components/common/LoadingScreen';
import { TransitionProvider } from './context/TransitionContext';

const normalizeUrl = (value?: string | null) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;
  try {
    return new URL(trimmed).toString().replace(/\/$/, '');
  } catch {
    return null;
  }
};

const shouldCanonicalRedirect = () => {
  if (typeof window === 'undefined') return null;

  const currentOrigin = normalizeUrl(window.location.origin);
  const configuredAppUrl = normalizeUrl(import.meta.env.VITE_APP_URL);

  if (!currentOrigin || !configuredAppUrl) return null;

  try {
    const currentUrl = new URL(currentOrigin);
    const configuredUrl = new URL(configuredAppUrl);
    const isLocal =
      currentUrl.hostname === 'localhost' ||
      currentUrl.hostname === '127.0.0.1';

    if (isLocal) return null;
    if (currentUrl.origin === configuredUrl.origin) return null;

    configuredUrl.pathname = window.location.pathname;
    configuredUrl.search = window.location.search;
    configuredUrl.hash = window.location.hash;
    return configuredUrl.toString();
  } catch {
    return null;
  }
};

// Navigation initializer component
const NavigationInitializer: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    NavigationService.initialize(navigate);
  }, [navigate]);

  return null;
};

/**
 * Main App component that serves as the entry point for the Frontend Landing Page
 * Wraps the application with necessary providers for theming, authentication, and routing
 * 
 * @returns {JSX.Element} The root application component with theme, auth, and routing setup
 */
const AppContent: React.FC = () => {
  const { loading: authLoading } = useAuth();
  const [introShouldRun, setIntroShouldRun] = React.useState<boolean>(true);
  const [introMinTimeElapsed, setIntroMinTimeElapsed] = React.useState(!introShouldRun);
  const [isIntroFullyRendered, setIsIntroFullyRendered] = React.useState(!introShouldRun);

  React.useEffect(() => {
    const canonicalTarget = shouldCanonicalRedirect();
    if (canonicalTarget && canonicalTarget !== window.location.href) {
      window.location.replace(canonicalTarget);
    }
  }, []);

  React.useEffect(() => {
    if (!introShouldRun) return;
    const timer = setTimeout(() => setIntroMinTimeElapsed(true), 11300);
    return () => clearTimeout(timer);
  }, [introShouldRun]);

  React.useEffect(() => {
    if (!introShouldRun) return;
    if (!isIntroFullyRendered) return;
    setIntroShouldRun(false);
  }, [introShouldRun, isIntroFullyRendered]);

  const introLoading = introShouldRun && !introMinTimeElapsed;
  const shouldRenderApp = !authLoading && !introShouldRun;

  return (
    <>
      {introShouldRun && !isIntroFullyRendered && (
        <LoadingScreen loading={introLoading} onTransitionComplete={() => setIsIntroFullyRendered(true)} />
      )}
      {!introShouldRun && authLoading && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
        </div>
      )}
      {shouldRenderApp && (
        <>
          <NavigationInitializer />
          <AuthHandler />
          <LoadingOverlay />
          <AppRoutes />
        </>
      )}
    </>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <UserProvider>
          <ProductProvider>
            <OrderProvider>
            <CartProvider>
              <WishlistProvider>
                <CommunityProvider>
                  <DashboardNotificationProvider>
                    <NotificationProvider>
                      <TransitionProvider>
                        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                          <AppContent />
                        </Router>
                      </TransitionProvider>
                    </NotificationProvider>
                  </DashboardNotificationProvider>
                </CommunityProvider>
              </WishlistProvider>
            </CartProvider>
            </OrderProvider>
          </ProductProvider>
        </UserProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
